import { Epic, ofType } from "redux-observable";
import { from, of } from "rxjs";
import {
  map,
  catchError,
  switchMap,
  debounceTime,
  delay,
} from "rxjs/operators";
import { message } from "antd";
import i18next from "../../../locales/i18n";
import { groupUserActions } from "./groupUserSlice";
import { mockGroups, mockUsers } from "../mockData";
import { Group } from "../api/groupUser.api";

/**
 * Epic: Fetch all groups (using mock data)
 */
export const fetchGroupsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(groupUserActions.fetchGroups.type),
    switchMap(() =>
      of(mockGroups).pipe(
        delay(500), // Simulate network delay
        map((groups) => groupUserActions.fetchGroupsSuccess(groups)),
        catchError((error) => {
          message.error(i18next.t("groupuser:fetchGroupsError"));
          return of(
            groupUserActions.fetchGroupsFailure(
              i18next.t("groupuser:fetchGroupsError"),
            ),
          );
        }),
      ),
    ),
  );

/**
 * Epic: Fetch all users (using mock data)
 */
export const fetchUsersEpic: Epic = (action$) =>
  action$.pipe(
    ofType(groupUserActions.fetchUsers.type),
    switchMap(() =>
      of(mockUsers).pipe(
        delay(500), // Simulate network delay
        map((users) => groupUserActions.fetchUsersSuccess(users)),
        catchError((error) => {
          message.error(i18next.t("groupuser:fetchUsersError"));
          return of(
            groupUserActions.fetchUsersFailure(
              i18next.t("groupuser:fetchUsersError"),
            ),
          );
        }),
      ),
    ),
  );

/**
 * Epic: Create new group (using mock data)
 */
export const createGroupEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(groupUserActions.createGroup.type),
    debounceTime(300),
    switchMap((action: any) => {
      const newGroup: Group = {
        id: `group-${Date.now()}`,
        name: action.payload.name,
        members: mockUsers.filter((user) =>
          action.payload.memberIds.includes(user.id),
        ),
      };

      return of(newGroup).pipe(
        delay(800), // Simulate network delay
        map((group) => {
          message.success(
            i18next.t("groupuser:createGroupSuccess", {
              groupName: group.name,
            }),
          );
          return groupUserActions.createGroupSuccess(group);
        }),
        catchError((error) => {
          message.error(i18next.t("groupuser:createGroupError"));
          return of(
            groupUserActions.createGroupFailure(
              i18next.t("groupuser:createGroupError"),
            ),
          );
        }),
      );
    }),
  );

/**
 * Epic: Add members to existing group (using mock data)
 */
export const addMembersToGroupEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(groupUserActions.addMembersToGroup.type),
    debounceTime(300),
    switchMap((action: any) => {
      const state = state$.value as any;
      const existingGroup = state.groupUser.groups.find(
        (g: Group) => g.id === action.payload.groupId,
      );

      if (!existingGroup) {
        message.error(i18next.t("groupuser:groupNotFound"));
        return of(
          groupUserActions.addMembersToGroupFailure(
            i18next.t("groupuser:groupNotFound"),
          ),
        );
      }

      const newMembers = mockUsers.filter((user) =>
        action.payload.memberIds.includes(user.id),
      );

      const updatedGroup: Group = {
        ...existingGroup,
        members: [...existingGroup.members, ...newMembers],
      };

      return of(updatedGroup).pipe(
        delay(800), // Simulate network delay
        map((group) => {
          message.success(
            i18next.t("groupuser:addMembersSuccess", {
              count: action.payload.memberIds.length,
            }),
          );
          return groupUserActions.addMembersToGroupSuccess(group);
        }),
        catchError((error) => {
          message.error(i18next.t("groupuser:addMembersError"));
          return of(
            groupUserActions.addMembersToGroupFailure(
              i18next.t("groupuser:addMembersError"),
            ),
          );
        }),
      );
    }),
  );

/**
 * Epic: Delete group (using mock data)
 */
export const deleteGroupEpic: Epic = (action$) =>
  action$.pipe(
    ofType(groupUserActions.deleteGroup.type),
    switchMap((action: any) =>
      of(action.payload).pipe(
        delay(600), // Simulate network delay
        map(() => {
          message.success(i18next.t("groupuser:deleteGroupSuccess"));
          return groupUserActions.deleteGroupSuccess(action.payload);
        }),
        catchError((error) => {
          message.error(i18next.t("groupuser:deleteGroupError"));
          return of(
            groupUserActions.deleteGroupFailure(
              i18next.t("groupuser:deleteGroupError"),
            ),
          );
        }),
      ),
    ),
  );

/**
 * Epic: Remove member from group (using mock data)
 */
export const removeMemberFromGroupEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(groupUserActions.removeMemberFromGroup.type),
    switchMap((action: any) => {
      const state = state$.value as any;
      const existingGroup = state.groupUser.groups.find(
        (g: Group) => g.id === action.payload.groupId,
      );

      if (!existingGroup) {
        message.error(i18next.t("groupuser:groupNotFound"));
        return of(
          groupUserActions.removeMemberFromGroupFailure(
            i18next.t("groupuser:groupNotFound"),
          ),
        );
      }

      const updatedGroup: Group = {
        ...existingGroup,
        members: existingGroup.members.filter(
          (m) => m.id !== action.payload.memberId,
        ),
      };

      return of(updatedGroup).pipe(
        delay(600), // Simulate network delay
        map((group) => {
          message.success(i18next.t("groupuser:removeMemberSuccess"));
          return groupUserActions.removeMemberFromGroupSuccess(group);
        }),
        catchError((error) => {
          message.error(i18next.t("groupuser:removeMemberError"));
          return of(
            groupUserActions.removeMemberFromGroupFailure(
              i18next.t("groupuser:removeMemberError"),
            ),
          );
        }),
      );
    }),
  );

/**
 * Combine all epics
 */
export const groupUserEpics = [
  fetchGroupsEpic,
  fetchUsersEpic,
  createGroupEpic,
  addMembersToGroupEpic,
  deleteGroupEpic,
  removeMemberFromGroupEpic,
];
